import { useEffect } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { IMAGES } from "@/data/site";

// Deterministic pseudo-random star field (no layout shift, stable between renders)
const rand = (i, n) => {
  const x = Math.sin(i * 127.1 + n * 311.7) * 43758.5453;
  return x - Math.floor(x);
};
const STARS = Array.from({ length: 42 }, (_, i) => ({
  left: rand(i, 1) * 100,
  top: rand(i, 2) * 100,
  size: 1 + rand(i, 3) * 1.7,
  delay: rand(i, 4) * 7,
  dur: 4 + rand(i, 5) * 6,
  desktopOnly: i % 2 === 0,
}));

const ORB_MASK = "radial-gradient(closest-side, rgba(0,0,0,1) 52%, rgba(0,0,0,0.55) 76%, transparent 97%)";

// Restrained star continuation for sections below the hero — fades out naturally
export const StarBleed = ({ count = 16, height = "h-[480px]", opacity = 0.8 }) => (
  <div
    aria-hidden="true"
    className={`pointer-events-none absolute inset-x-0 top-0 ${height} overflow-hidden`}
    style={{
      opacity,
      maskImage: "linear-gradient(to bottom, rgba(0,0,0,1), transparent 92%)",
      WebkitMaskImage: "linear-gradient(to bottom, rgba(0,0,0,1), transparent 92%)",
    }}
    data-testid="star-bleed"
  >
    {STARS.slice(0, count).map((s, i) => (
      <span
        key={i}
        className="absolute rounded-full bg-white star-twinkle"
        style={{
          left: `${s.left}%`, top: `${s.top}%`,
          width: `${Math.min(s.size, 1.8)}px`, height: `${Math.min(s.size, 1.8)}px`,
          animationDelay: `${s.delay}s`, animationDuration: `${s.dur}s`,
        }}
      />
    ))}
  </div>
);

export const CosmicField = () => {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 32, damping: 22 });
  const sy = useSpring(my, { stiffness: 32, damping: 22 });

  // layered parallax depths (max ≈ ±18px on the orb, less on the field)
  const fieldX = useTransform(sx, (v) => v * -10);
  const fieldY = useTransform(sy, (v) => v * -7);
  const orbX = useTransform(sx, (v) => v * 18);
  const orbY = useTransform(sy, (v) => v * 12);
  const starX = useTransform(sx, (v) => v * -22);
  const starY = useTransform(sy, (v) => v * -14);

  useEffect(() => {
    const fine = window.matchMedia("(pointer: fine)").matches;
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (!fine || reduced || window.innerWidth < 1024) return;
    const onMove = (e) => {
      mx.set(e.clientX / window.innerWidth - 0.5);
      my.set(e.clientY / window.innerHeight - 0.5);
    };
    window.addEventListener("mousemove", onMove, { passive: true });
    return () => window.removeEventListener("mousemove", onMove);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none" data-testid="cosmic-field" aria-hidden="true">
      {/* Approved reference environment plate: energy flow (left) + star field + Earth horizon (bottom) */}
      <motion.div style={{ x: fieldX, y: fieldY }} className="absolute -inset-6">
        <img
          src={IMAGES.heroPlate}
          alt=""
          className="h-full w-full object-cover object-[center_70%]"
        />
        {/* gentle top vignette so the nav stays crisp */}
        <div className="absolute inset-x-0 top-0 h-40 bg-gradient-to-b from-[#0a0a0f]/80 to-transparent" />
        {/* soft blend into the content below */}
        <div className="absolute inset-x-0 bottom-0 h-[22%] bg-gradient-to-b from-transparent via-[#0a0a0f]/60 to-[#0a0a0f]" />
        {/* very slow atmospheric shimmer along the Earth rim */}
        <div
          className="absolute inset-x-0 bottom-0 h-1/2 horizon-shimmer"
          data-testid="cosmic-horizon"
          style={{ background: "radial-gradient(62% 42% at 50% 100%, rgba(56,189,248,0.14), rgba(139,92,246,0.06) 55%, transparent 80%)" }}
        />
      </motion.div>

      {/* Drifting star / particle layer */}
      <motion.div style={{ x: starX, y: starY }} className="absolute inset-0 cf-drift">
        {STARS.map((s, i) => (
          <span
            key={i}
            className={`absolute rounded-full bg-white star-twinkle ${s.desktopOnly ? "hidden lg:block" : ""}`}
            style={{
              left: `${s.left}%`, top: `${s.top}%`,
              width: `${s.size}px`, height: `${s.size}px`,
              animationDelay: `${s.delay}s`, animationDuration: `${s.dur}s`,
            }}
          />
        ))}
      </motion.div>

      {/* Breathing background glows */}
      <div className="absolute -top-24 left-[16%] h-[440px] w-[440px] glow-violet glow-breathe" />
      <div className="absolute top-[30%] right-[4%] h-[520px] w-[520px] glow-cyan glow-breathe" style={{ animationDelay: "-6s" }} />

      {/* The intelligence orb (A monogram) — primary anchor, borderless, blended */}
      <motion.div
        style={{ x: orbX, y: orbY }}
        className="absolute aspect-square
                   w-[92vw] max-w-none -right-[24vw] top-[1%] opacity-75
                   sm:w-[64vw] sm:-right-[14vw] sm:top-[3%] sm:opacity-85
                   lg:w-[min(46vw,780px)] lg:right-[1%] lg:top-[7%] lg:opacity-100"
        data-testid="cosmic-orb"
      >
        <img
          src={IMAGES.orbA}
          alt=""
          className="h-full w-full object-cover orb-breathe"
          style={{ mixBlendMode: "screen", maskImage: ORB_MASK, WebkitMaskImage: ORB_MASK }}
        />

        {/* Orbital rings — counter-rotating at different speeds for depth */}
        <div className="absolute inset-0 cf-spin">
          <div className="absolute left-1/2 top-1/2 h-[46%] w-[88%] -translate-x-1/2 -translate-y-1/2 rounded-[50%] border border-cyan-300/[0.09]" style={{ rotate: "-18deg" }} />
        </div>
        <div className="absolute inset-0 cf-spin-rev">
          <div className="absolute left-1/2 top-1/2 h-[38%] w-[74%] -translate-x-1/2 -translate-y-1/2 rounded-[50%] border border-violet-300/[0.08]" style={{ rotate: "-24deg" }} />
        </div>

        {/* Flowing energy trail along an orbital path */}
        <svg className="absolute inset-0 h-full w-full" viewBox="0 0 100 100" fill="none">
          <defs>
            <linearGradient id="cf-energy" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0" stopColor="#22d3ee" stopOpacity="0" />
              <stop offset="0.4" stopColor="#22d3ee" stopOpacity="0.55" />
              <stop offset="0.7" stopColor="#a78bfa" stopOpacity="0.5" />
              <stop offset="1" stopColor="#a78bfa" stopOpacity="0" />
            </linearGradient>
          </defs>
          <ellipse
            cx="50" cy="50" rx="42" ry="17.5"
            stroke="url(#cf-energy)" strokeWidth="0.35" strokeLinecap="round"
            pathLength="100" strokeDasharray="26 74"
            className="energy-dash"
            style={{ transformOrigin: "50% 50%", transform: "rotate(-18deg)" }}
          />
        </svg>
      </motion.div>

      {/* Fine grid + readability scrims over the artwork */}
      <div className="absolute inset-0 bg-grid opacity-60" />
      <div className="absolute inset-0 hidden lg:block bg-[linear-gradient(to_right,rgba(10,10,15,0.92)_0%,rgba(10,10,15,0.6)_42%,rgba(10,10,15,0.18)_62%,transparent_78%)]" />
      <div className="absolute inset-0 hidden sm:block lg:hidden bg-gradient-to-r from-[#0a0a0f]/85 via-[#0a0a0f]/45 to-transparent" />
      <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0f]/70 via-[#0a0a0f]/45 to-[#0a0a0f]/25 sm:hidden" />

    </div>
  );
};
